import { formatCalendarDate, formatInstantDay, parseInstantDate } from '../calendar-date';

/**
 * Explicit field-name → format-type overrides.
 * These take priority over the heuristic currency/date detection below.
 *
 * 'number-decimal' → plain number with 1 decimal (hours, utilization)
 * 'number-integer' → plain integer (counts, days)
 * 'currency'       → forced currency formatting
 */
const FIELD_FORMAT_OVERRIDES: Record<string, 'number-decimal' | 'number-integer' | 'currency'> = {
  // Hours / duration fields — NOT currency
  total_hours:    'number-decimal',
  hours_used:     'number-decimal',
  hours_worked:   'number-decimal',
  // Count / quantity fields — plain integers
  days_worked:    'number-integer',
  quantity:       'number-integer',
  jobs_count:     'number-integer',
  jobs_worked:    'number-integer',
  total_days:     'number-integer',
  total_jobs:     'number-integer',
  // Explicit currency fields
  revenue:        'currency',
  total_revenue:  'currency',
  total_billed:   'currency',
  total_paid:     'currency',
  total_outstanding: 'currency',
  pay_earned:     'currency',
  unit_price:     'currency',
  amount:         'currency',
};

const CURRENCY_HINTS = [
  'revenue','amount','price','cost','paid','owed','outstanding',
  'total','subtotal','balance','fee','tax','gst','hst','pst',
  'wage','pay','salary','income','expense',
];

/**
 * Two kinds of temporal column arrive through this one function, and they need
 * opposite treatment. Lumping them under one DATE_SUFFIXES list and handing all
 * of them to `new Date()` printed every calendar day one day early.
 *
 * `_at` is an INSTANT — '2026-07-15T03:30:00Z'. It is printed as the day it
 * fell on in the TENANT's zone (`companies.time_zone`), never the viewer's
 * device zone — so the table and the export agree for every reader.
 *
 * `_date` / `_on` are CALENDAR DAYS — bare 'YYYY-MM-DD'. This file's own
 * docblock above gives the live example, curl-verified: `expiry_date:
 * "2026-09-14"`. `new Date('2026-09-14')` is UTC midnight, which is 18:00 on
 * the 13th in Alberta, so the certification column read "Sep 13, 2026" for
 * every tenant we have — on screen AND in the CSV/PDF export, since
 * UnifiedReports builds both from this function.
 */
const INSTANT_SUFFIXES = ['_at'];
const CALENDAR_DAY_SUFFIXES = ['_date', '_on'];

type FormatOptions = {
  currency?: 'CAD' | 'USD';
  locale?: string;
};

/**
 * Per-field formatters for array-of-objects columns.
 *
 * Actual API shapes (curl-verified 2026-05-21):
 *
 * certifications (GET /reports/by-crew):
 *   [{ user_id, certification_type, expiry_date, cert_status }]
 *   e.g. { "certification_type": "H2S_alive", "expiry_date": "2026-09-14", "cert_status": "active" }
 *
 * equipment (GET /reports/by-well):
 *   [{ well_name, equipment_id, equipment_name, equipment_type }]
 *   e.g. { "equipment_name": "Tong-03", "equipment_type": "Power Tongs" }
 *
 * crew (GET /reports/by-well):
 *   [{ well_name, user_id, first_name, last_name, initials, role_on_job }]
 *   e.g. { "first_name": "Cody", "last_name": "Harrigan", "role_on_job": null }
 */
const ARRAY_OBJECT_FORMATTERS: Record<string, (item: Record<string, unknown>) => string> = {
  certifications: (cert) => {
    const type = cert.certification_type;
    return typeof type === 'string' ? type.replace(/_/g, ' ') : '—';
  },
  equipment: (eq) => {
    const name = eq.equipment_name ?? eq.name;
    return typeof name === 'string' ? name : '—';
  },
  crew: (member) => {
    const first = member.first_name ?? '';
    const last = member.last_name ?? '';
    const full = `${first} ${last}`.trim();
    return full || (typeof member.name === 'string' ? member.name : '—');
  },
};

export function formatCellValue(
  column: string,
  value: unknown,
  /** The tenant's IANA zone (useTenantTimeZone()). Used for `_at` columns only. */
  timeZone: string | null,
  opts: FormatOptions = {}
): string {
  if (value === null || value === undefined || value === '') return '—';

  // Array handling — must come before primitive checks
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    // Array of primitives
    if (typeof value[0] !== 'object' || value[0] === null) {
      return value.join(', ');
    }
    // Array of objects — use per-field formatter or fall back to "—"
    const formatter = ARRAY_OBJECT_FORMATTERS[column];
    if (formatter) {
      return value.map(formatter).join(', ');
    }
    return '—';
  }

  // Plain object safety net — never leak "[object Object]"
  if (typeof value === 'object') return '—';

  const colLower = column.toLowerCase();
  const locale = opts.locale ?? 'en-CA';

  // Explicit field-name override — takes priority over heuristic detection
  const override = FIELD_FORMAT_OVERRIDES[colLower];
  if (override) {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (Number.isNaN(num)) return String(value);
    if (override === 'currency') {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: opts.currency ?? 'CAD',
      }).format(num);
    }
    if (override === 'number-decimal') {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(num);
    }
    // number-integer
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  }

  // Currency detection (heuristic fallback)
  const isCurrencyByName = CURRENCY_HINTS.some(h => colLower.includes(h));
  const explicitCAD = colLower.endsWith('_cad');
  const explicitUSD = colLower.endsWith('_usd');

  if (isCurrencyByName || explicitCAD || explicitUSD) {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (Number.isNaN(num)) return String(value);
    const currency = explicitUSD ? 'USD' : (explicitCAD ? 'CAD' : (opts.currency ?? 'CAD'));
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(num);
  }

  // Calendar-day detection — parsed at LOCAL midnight so the day the column
  // holds is the day that prints. Falls back to the raw string rather than
  // guessing, so an unparseable value is visible instead of becoming today.
  if (CALENDAR_DAY_SUFFIXES.some(suffix => colLower.endsWith(suffix))) {
    return (
      formatCalendarDate(String(value), { year: 'numeric', month: 'short', day: 'numeric' }, locale)
      ?? String(value)
    );
  }

  // Instant detection — the tenant's day; see the note on the suffix lists
  // above. An unparseable value is shown raw, as the calendar-day branch does;
  // a parseable one with no known zone is absent, never the device's day.
  if (INSTANT_SUFFIXES.some(suffix => colLower.endsWith(suffix))) {
    if (!parseInstantDate(String(value))) return String(value);
    return formatInstantDay(String(value), timeZone, {
      year: 'numeric', month: 'short', day: 'numeric',
    }, locale) ?? '—';
  }

  // Plain numbers
  if (typeof value === 'number') {
    return new Intl.NumberFormat(locale).format(value);
  }

  return String(value);
}
